import {computed, onBeforeUnmount, onMounted, Ref, ref} from '@vue/composition-api';
import {useRouter} from '@/router';
import {RouterAliases} from '@/router/routerAliases';

import {useSolutionViews} from '../solution-views/solution-views';
import {
  TIME_FILTER_UPDATED,
  TIME_FILTER_UPDATE_STARTED,
  TRAVELERS_FILTER_UPDATED,
  TRAVELERS_FILTER_UPDATE_STARTED,
  useSolutionFilters,
  VueWithModal,
} from '../solution-views/summary-filters';
import {useRecommendationDetails} from '../solution-views/recommendation-details/recommendation-details';
import {
  CandidatePoiItem,
  ConstraintType,
  getBeginningOfTheDay,
  getNextDay,
  getSolutionDatesRange,
  getSolutionDays,
  IDateDescription,
  ISolution,
  Timeslot,
  UserActivityCategoryEnum,
} from '@mobi-components/global-solution';
import {
  addManualActivity,
  dropSolution,
  getAmexSolution,
  getSelectedTheme,
  getSessionId,
  getSolutionRecommendations,
  getTripId,
  isAdditionalRecommendationsDiningInputRequired,
  isAdditionalRecommendationsExploreInputRequired,
  updateSolution,
} from '@/store/state/helpers';
import {generateEmptyActivityItem, ProblmSubmissionRoute, RecommendationContext} from '@/store/state/global.types';
import BookingDialogVue from '../summary-page/summary-booking/booking-modal.vue';
import {LunchTime} from '@/store/api/reschedule-activity';
import {getavailableDatesForPoi} from './exp-list/utils';
import {useMealToggle} from '@/views/amex/components/explore-page/details-view/POIDetailsView/components/meal-toggle';
import {getAvailableTimeSlots} from '@/store/api/download-available-timeslots';
import {FilterKind} from '@mobi-components/amex-bluesky-components';
import {fixDate} from '@/store/state/fix-solution-dates';
import {APIService} from '@/services/api.service';
import {useDetailsHeader} from '../solution-views/details-header-handler';
import {RoutingCategory} from '../solution-views/details-header-handler';
import {startOfDay} from '@mobi-components/global-date-time-picker';

interface AddExp {
  timeslot: [number, number];
  lunchTime: LunchTime;
  poiId: string;
}

const useRecommendedOptions = (
  solutionDates: Ref<readonly IDateDescription[]>,
  candidates: Ref<readonly CandidatePoiItem[]>,
  solution: Ref<ISolution | null>,
  addExperience: (e: AddExp, slots: Map<string, [number, number]>) => void,
) => {
  const pendingActivity = ref<CandidatePoiItem | null>(null);
  const isTimeSlotsPending = ref(false);
  const availableTimeSlots = ref<Timeslot[]>([]);
  const category = ref(UserActivityCategoryEnum.NONE);
  const {onMealChange, getSelectedLunchTime, ...rest} = useMealToggle(category);
  const slotsMap = ref<Map<string, [number, number]>>(new Map());

  const allRecommendationsWithDays = computed<Map<string, string[]>>(() => {
    const availablePois = getSolutionRecommendations();
    const dates = solutionDates.value.map((x) => x.date);

    return availablePois.reduce((res, x) => {
      const datesForPoi = new Set(getavailableDatesForPoi(x, dates).map((date) => date.valueOf()));
      const labels = solutionDates.value.filter((x) => datesForPoi.has(x.date.valueOf())).map((x) => x.label);
      res.set(x.source.content?.id || '', labels);
      return res;
    }, new Map());
  });

  const getRecommendedDayLabel = (activityId: string) => {
    const options = allRecommendationsWithDays.value.get(activityId) || [];
    if (options.length) {
      return options[0];
    }
    return '';
  };

  const isMealSelected = computed(() => {
    const category =
      (pendingActivity.value?.categories?.length && pendingActivity.value?.categories[0]) ||
      UserActivityCategoryEnum.NONE;
    return category === UserActivityCategoryEnum.DINING;
  });

  const pendingActivityId = computed(() => {
    return pendingActivity.value?.id || '';
  });

  const onCloseScheduler = () => {
    pendingActivity.value = null;
  };

  const onRescheduleClick = async ({timeslot, event}: {timeslot: Timeslot | null; event: Event}) => {
    event.stopPropagation();
    const activityId = pendingActivityId.value;
    pendingActivity.value = null;
    const dates: number[] = (solution.value?.activities || [])
      // @ts-ignore
      .map((x) => [x.original_start_time, x.original_end_time])
      .flat();
    const min = Math.min(...dates) || 0;
    const max = Math.max(...dates) || 0;
    const date = timeslot || [min, max];
    const relatedSlot = date;
    const startTime = getBeginningOfTheDay(new Date(relatedSlot[1] * 1000)).valueOf();
    const endTime = getNextDay(new Date(relatedSlot[1])).valueOf();
    return addExperience(
      {poiId: activityId, timeslot: [startTime, endTime], lunchTime: getSelectedLunchTime()},
      slotsMap.value,
    );
  };

  const openRescheduleOptions = async (poi: CandidatePoiItem) => {
    onMealChange(LunchTime.ANY);
    availableTimeSlots.value = [];
    isTimeSlotsPending.value = true;
    try {
      const goalItem = generateEmptyActivityItem(poi.id, poi.name, category.value, ConstraintType.NOT_APPLICABLE, 1800);
      goalItem.candidate_information = {candidates: [poi], search: null, selection: null};
      const timeslots = await getAvailableTimeSlots(goalItem, getSessionId());
      const timezone = poi.location.time_zone;
      const adjustedSlots: Timeslot[] = timeslots.map((x) => {
        const start = fixDate(x[0], timezone);
        const end = fixDate(x[1], timezone);
        return [start, end];
      });
      slotsMap.value = new Map(
        adjustedSlots.map((x, i) => {
          return [`${x[0]}-${x[1]}`, timeslots[i]];
        }),
      );
      availableTimeSlots.value = adjustedSlots;
    } catch {
      availableTimeSlots.value = [];
    }
    isTimeSlotsPending.value = false;
  };

  const onGetItemOptions = (alternativeHotelId: string) => {
    pendingActivity.value = candidates.value.find((x) => x.id === alternativeHotelId) || null;
    if (pendingActivity.value) {
      category.value = pendingActivity.value.categories?.length
        ? pendingActivity.value.categories[0]
        : UserActivityCategoryEnum.NONE;
      openRescheduleOptions(pendingActivity.value);
    }
  };

  return {
    pendingActivity,
    pendingActivityId,
    isTimeSlotsPending,
    availableTimeSlots,
    allRecommendationsWithDays,
    getRecommendedDayLabel,
    onCloseScheduler,
    onRescheduleClick,
    onGetItemOptions,
    onMealChange,
    ...rest,
    isMealSelected, // overwriting original isMealSelected in ...rest
  };
};

export function useExploreView(root: VueWithModal) {
  const {
    solution,
    solutionDatesRange,
    getPoiForSelectedItem,
    availableRecommendations,
    activityForSelectedPoiId,
    ...restOfSolutionViews
  } = useSolutionViews(root);
  const {travelersFilter, onFilterClick} = useSolutionFilters(root);
  const recommendationsScrollTrigger = ref<number>(new Date().valueOf());
  const isRecommendationsLoading = ref(true);
  const router = useRouter();

  const recommendationsToggle = ref(getSelectedTheme() !== 'Custom');

  const onBookingOpen = () => {
    root.$modal.show(BookingDialogVue, {});
  };

  const numberOfTravelers = computed(() => {
    const {adults, children} = travelersFilter;
    return adults + children;
  });

  const isFlightsActive = computed(() => {
    return router.currentRoute.name === RouterAliases.FLIGHT_EXPLORE_VIEW;
  });

  const isHotelsActive = computed(() => {
    return router.currentRoute?.params?.category === UserActivityCategoryEnum.ACCOMMODATION;
  });

  const selectedPoiCategory = computed(() => {
    return (getPoiForSelectedItem.value?.categories || []).find(Boolean);
  });

  const {onOpenCategory} = useDetailsHeader();

  const backToExplore = () => {
    if (selectedPoiCategory.value) {
      onOpenCategory({cat: selectedPoiCategory.value as RoutingCategory});
    } else {
      onShowRecommendation('');
    }
  };

  const detailsCategoryText = computed(() => {
    const category = selectedPoiCategory.value;
    switch (category) {
      case UserActivityCategoryEnum.ACCOMMODATION:
        return 'Stay';
      case UserActivityCategoryEnum.EXPERIENCE:
        return 'Experience';
      case UserActivityCategoryEnum.DINING:
        return 'Dining';
      default:
        return '';
    }
  });

  const onAddManyExperiences = async (items: RecommendationContext[]) => {
    const promises = items.map((x) => addManualActivity(x));
    await Promise.all(promises);

    try {
      solutionDatesRange.value.forEach((x) => {
        root.$emit('day-loading', {day: x.label, name: ''});
      });
      const newTripId = getTripId();
      await updateSolution(newTripId, ProblmSubmissionRoute.SOLUTION, []);
    } catch {
    } finally {
      solutionDatesRange.value.forEach((x) => {
        root.$emit('day-loaded', x.label);
      });
    }
  };

  const addExperience = async ({
    timeslot,
    lunchTime,
    poiId: poiIdToAdd,
  }: {
    timeslot: Timeslot | null;
    lunchTime: LunchTime;
    poiId: string;
  }) => {
    const additionalRecommendations = solutionRecommendations.value.map((x) => x.source.content);
    const index = solutionRecommendations.value.findIndex((x) => x.source?.content?.id === poiIdToAdd);
    const prev = solutionRecommendations.value[index - 1];
    const next = solutionRecommendations.value[index + 1];
    const activityIdToSelect = next?.id || prev?.id;
    const alt = (availableRecommendations.value || [])
      .concat((additionalRecommendations as unknown) as RecommendationContext[])
      .find((x) => x.id === poiIdToAdd);
    const activity = solution.value?.activities.find(
      (x) => x.id === poiIdToAdd || x.source?.candidate_id === poiIdToAdd,
    );
    const selected = alt || activity;
    const dates: number[] = (solution.value?.activities || [])
      .map((x) => [x.scheduled_start_time, x.scheduled_end_time])
      .flat();
    const min = Math.min(...dates) || 0;
    const max = Math.max(...dates) || 0;
    const date = timeslot || [min, max];
    const startTime = getBeginningOfTheDay(new Date(date[1] * 1000)).valueOf();
    const endTime = getNextDay(new Date(date[1] * 1000)).valueOf();
    const days = solutionDatesRange.value.filter((x) => {
      const dayStart = x.date.valueOf();
      const dayEnd = getNextDay(x.date).valueOf();
      return (
        (startTime <= dayStart && dayStart <= endTime) ||
        (startTime <= dayEnd && dayEnd <= endTime) ||
        (startTime >= dayStart && startTime <= dayEnd) ||
        (endTime >= dayStart && endTime <= dayEnd)
      );
    });
    if (selected) {
      isRecommendationsLoading.value = true;
      // @ts-ignore
      selected.start_timestamp = startTime;
      // @ts-ignore
      selected.end_timestamp = endTime;
      // @ts-ignore
      days.forEach((x) => {
        root.$emit('day-loading', {day: x.label, name: selected.name});
      });
      try {
        // @ts-ignore
        await addManualActivity(selected);
        const newTripId = getTripId();
        await updateSolution(newTripId, ProblmSubmissionRoute.SOLUTION, [], lunchTime);
        onRecommendationsOpen(activityIdToSelect);
      } catch {
      } finally {
        isRecommendationsLoading.value = false;
        days.forEach((x) => {
          root.$emit('day-loaded', x.label);
        });
      }
    }
    recommendationsScrollTrigger.value = new Date().valueOf();
  };

  const {
    onShowRecommendation,
    solutionRecommendations,
    solutionRecommendationsCandidates,
    selectedRecommendationPoi,
    isRecommendationDetailsVisible,
    ...restOfRecommendations
  } = useRecommendationDetails(onAddManyExperiences, root);

  const recommendationHeader = computed(() => {
    const category =
      selectedRecommendationPoi.value?.categories[0] ||
      activityForSelectedPoiId.value?.category ||
      UserActivityCategoryEnum.NONE;
    let cat = '';
    switch (category) {
      case UserActivityCategoryEnum.ACCOMMODATION:
        cat = 'Stay';
        break;
      case UserActivityCategoryEnum.EXPERIENCE:
        cat = 'Experience';
        break;
      case UserActivityCategoryEnum.DINING:
        cat = 'Dinner';
        break;
    }
    const range = getSolutionDatesRange(solution.value);
    const day = getSolutionDays(range)
      .map((d, i) => ({date: d, index: i + 1}))
      .find((d) => {
        // @ts-ignore
        const date = startOfDay(new Date(selectedRecommendationPoi.value?.start_timestamp || 0 * 1000));
        const start = startOfDay(d.date);
        return date.valueOf() === start.valueOf();
      });

    return day ? `${cat} on Day ${day.index}` : cat;
  });

  const isExperiencesActive = computed(() => {
    const router = useRouter();
    const category = router.currentRoute?.params?.category || UserActivityCategoryEnum.NONE;
    return category === UserActivityCategoryEnum.EXPERIENCE || category === UserActivityCategoryEnum.DINING;
  });

  const onQuickAdd = ({
    timeslot,
    lunchTime,
    poiId: poiIdToAdd,
  }: {
    timeslot: Timeslot | null;
    lunchTime: LunchTime;
    poiId: string;
  }) => {
    return addExperience({
      timeslot,
      lunchTime,
      poiId: poiIdToAdd,
    });
  };

  const onRecommendationsOpen = async (id?: string) => {
    if (!solutionRecommendations.value || !solutionRecommendations.value.length) {
      recommendationsToggle.value = false;
      onShowRecommendation('');
      return;
    }
    const first = solutionRecommendations.value.find((x) => (id ? x.id === id : true));
    recommendationsToggle.value = true;
    onShowRecommendation(first?.source.candidate_id || '');
  };

  const closeRecommendationsView = () => {
    onShowRecommendation('');
  };

  const setRecommendationsLoading = () => {
    closeRecommendationsView();
    isRecommendationsLoading.value = true;
  };
  const disableRecommendationsLoading = () => {
    isRecommendationsLoading.value = false;
  };

  onMounted(() => {
    root.$on(TIME_FILTER_UPDATE_STARTED, setRecommendationsLoading);
    root.$on(TRAVELERS_FILTER_UPDATE_STARTED, setRecommendationsLoading);
    root.$on(TIME_FILTER_UPDATED, disableRecommendationsLoading);
    root.$on(TRAVELERS_FILTER_UPDATED, disableRecommendationsLoading);
  });

  onBeforeUnmount(() => {
    root.$off(TIME_FILTER_UPDATE_STARTED, setRecommendationsLoading);
    root.$off(TRAVELERS_FILTER_UPDATE_STARTED, setRecommendationsLoading);
    root.$off(TIME_FILTER_UPDATED, disableRecommendationsLoading);
    root.$off(TRAVELERS_FILTER_UPDATED, disableRecommendationsLoading);
  });

  const closeRecommendations = () => {
    recommendationsToggle.value = false;
    closeRecommendationsView();
  };

  const onRecommendationsChange = () => {
    recommendationsToggle.value = !recommendationsToggle.value;
    if (recommendationsToggle.value) {
      onRecommendationsOpen();
    } else {
      closeRecommendations();
    }
  };

  const recommendationRequirements = computed(() => {
    const dining = (isAdditionalRecommendationsDiningInputRequired() && UserActivityCategoryEnum.DINING) || null;
    const explore = (isAdditionalRecommendationsExploreInputRequired() && UserActivityCategoryEnum.EXPERIENCE) || null;
    return [dining, explore].filter(Boolean);
  });

  // restoreTripFromMyTrips();
  // restoreTripFromTripSample();

  // restoreTripFromMyTrips().then(() => {
  //   const solution = getAmexSolution();
  //   if (!solution) {
  //     restoreTripFromTripSample();
  //   }
  // });

  const onShowRecommendationDetails = (id: string) => {
    onShowRecommendation(id);
    recommendationsScrollTrigger.value = new Date().valueOf();
  };

  const onTimeFrameFilterClick = (filter: {type: FilterKind}) => {
    onShowRecommendation('');
    onFilterClick(filter);
  };

  const getCategoryFromPath = () => {
    const path = useRouter();
    switch (path.currentRoute.name) {
      case RouterAliases.ACCOMMODATION_EXPLORE_VIEW:
        return UserActivityCategoryEnum.ACCOMMODATION;
      case RouterAliases.FLIGHT_EXPLORE_VIEW:
        return UserActivityCategoryEnum.FLIGHT;
      case RouterAliases.EXPERIENCE_EXPLORE_VIEW:
        return UserActivityCategoryEnum.EXPERIENCE;
      case RouterAliases.DINNING_EXPLORE_VIEW:
        return UserActivityCategoryEnum.DINING;
      default:
        return '';
    }
  };

  const category = computed(() => {
    const pathCategory = getCategoryFromPath();
    return selectedPoiCategory.value || pathCategory;
  });

  return {
    recommendationsToggle,
    detailsCategoryText,
    backToExplore,
    solution,
    ...restOfSolutionViews,
    onFilterClick,
    onTimeFrameFilterClick,
    isFlightsActive,
    isHotelsActive,
    isExperiencesActive,
    onRecommendationsOpen,
    onRecommendationsChange,
    solutionRecommendations,
    onShowRecommendationDetails,
    ...restOfRecommendations,
    closeRecommendations,
    recommendationRequirements,
    onQuickAdd,
    onBookingOpen,
    numberOfTravelers,
    recommendationsScrollTrigger,
    selectedRecommendationPoi,
    recommendationHeader,
    isRecommendationDetailsVisible,
    ...useRecommendedOptions(solutionDatesRange, solutionRecommendationsCandidates, solution, addExperience),
    solutionRecommendationsCandidates,
    solutionDatesRange,
    isRecommendationsLoading,
    closeRecommendationsView,
    category,
  };
}

export const useSessionValidation = () => {
  const validateSession = async () => {
    try {
      const sessionId = getSessionId();
      const solution = getAmexSolution();
      const {result: sessionStatus} = await APIService.get<{result: boolean}>(
        'session-liveness',
        {
          params: {
            session_id: sessionId,
          },
        },
        true,
      );
      if (!sessionStatus && sessionId && solution) {
        await dropSolution();
        window.location.reload();
      }
    } catch {}
  };

  const intervalHandler = ref(0);

  onMounted(() => {
    intervalHandler.value = setInterval(validateSession, 1000 * 60);
  });

  onBeforeUnmount(() => {
    clearInterval(intervalHandler.value);
  });
};
